/**
 * portalLink.ts
 *
 * Cross-app navigation between the website (tenants) and the portal
 * (landlords, agents, admins, etc.).
 *
 * Authentication is carried by the HttpOnly `srs_token` cookie set by the
 * backend. The portal bootstraps via GET /auth/me on startup — no token
 * is ever passed in the URL.
 */

import { isPortalRole } from './rolePrecedence';

// Cross-app URLs are environment-driven only — no hardcoded hosts/ports. Each
// environment supplies these via .env (REACT_APP_PORTAL_URL / REACT_APP_WEBSITE_URL).
const PORTAL_URL  = (process.env.REACT_APP_PORTAL_URL  ?? '').replace(/\/$/, '');
const WEBSITE_URL = (process.env.REACT_APP_WEBSITE_URL ?? '').replace(/\/$/, '');

if (!PORTAL_URL || !WEBSITE_URL) {
  // Surface a misconfigured build immediately rather than silently producing
  // broken cross-app links.
  // eslint-disable-next-line no-console
  console.error('Cross-app URLs missing: set REACT_APP_PORTAL_URL and REACT_APP_WEBSITE_URL');
}

/**
 * Returns true if the user's effective (highest-precedence) role belongs to the
 * portal (landlord, agent, agency, admin, super_admin). Tenants stay on the
 * website.
 *
 * Delegates to the shared role-precedence utility so the website and portal
 * resolve multi-role users identically (SUPER_ADMIN → … → TENANT).
 */
export function isPortalUser(user: { roles?: Array<{ name?: string; role_name?: string }> }): boolean {
  return isPortalRole(user);
}

/**
 * Build the portal landing URL with a returnPath query parameter.
 * Authentication is carried by the HttpOnly cookie — no token in the URL.
 *
 * Format: PORTAL_URL?returnPath=<path>
 *
 * The portal reads returnPath on startup and navigates there after the
 * /auth/me bootstrap confirms a valid session.
 */
export function buildPortalUrl(returnPath: string = '/dashboard'): string {
  const decoded = (() => { try
    { return decodeURIComponent(returnPath); }
    catch { return '/dashboard'; } })();
  const safePath = decoded.startsWith('/') && !decoded.startsWith('//')
    ? decoded
    : '/dashboard';
  return `${PORTAL_URL}?returnPath=${encodeURIComponent(safePath)}`;
}

/**
 * Navigate to the portal. The HttpOnly cookie carries the session.
 */
export function goToPortal(path: string = '/dashboard'): void {
  window.location.href = buildPortalUrl(path);
}

export { PORTAL_URL, WEBSITE_URL };
